/**
 * The access token — IN MEMORY, and nowhere else (issue #45).
 *
 * There is no `localStorage` in this file and there must never be one. The
 * platform renders user-generated content — Pearls, tutor output, mastery
 * answers, markdown from the content pipeline — which is real XSS surface, and
 * a token in web storage is a token any injected script can read and exfiltrate
 * at leisure. A module-scoped variable dies with the tab, is invisible to other
 * origins, and cannot be enumerated. `accessToken.test.ts` asserts the negative
 * so a future convenience commit has to argue with a red test.
 *
 * The refresh token is not here either, and for the same reason turned inside
 * out: on the web it lives in an httpOnly cookie this code CANNOT read, which
 * is precisely what makes it safe to keep for weeks. Only a native client ever
 * holds one in code, and it puts it in the Keychain / Keystore.
 *
 * Losing the access token on reload is therefore by design, not a gap: page
 * load has no credential, the first request 401s, and `refreshAccessToken()`
 * trades the cookie for a new one. `request()` does that automatically.
 */
import type { RefreshResponse } from '@cusp/contracts';
import { getApiBaseUrl } from './config.js';

let accessToken: string | null = null;

/** Native clients hold their own refresh token; web clients never see one. */
let refreshToken: string | null = null;

/** One in-flight refresh, shared. See `refreshAccessToken`. */
let inFlight: Promise<string | null> | null = null;

/**
 * WHICH CREDENTIALS THESE ARE — bumped every time they change.
 *
 * A refresh is a network round trip, and the credentials can be replaced while
 * it is in the air. Two races, both silent and both bad:
 *
 *  · SIGN-OUT. Refresh starts → the user signs out → the response arrives and
 *    stores a working access token. The UI says signed out, the tab holds a
 *    live bearer credential for up to another ten minutes, and nothing looks
 *    wrong. That defeats the one guarantee logout makes.
 *  · IDENTITY CHANGE. Refresh starts → someone signs in as a different user →
 *    the old response lands last and overwrites the new user's token with the
 *    previous user's. Now requests go out as the wrong person.
 *
 * Dropping the promise reference is not enough to stop either: the request is
 * already running and its continuation still holds a reference to `setTokens`.
 * So every completion re-checks the generation it started in and applies
 * nothing if the world moved on. Cheap, and it makes "stale" a decidable
 * question rather than a timing accident.
 */
let generation = 0;

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Adopt a token pair. `refreshToken` is present only for native clients — on
 * the web the field is absent by construction, so this quietly stays null.
 */
export function setTokens(tokens: { accessToken: string; refreshToken?: string }): void {
  accessToken = tokens.accessToken;
  if (tokens.refreshToken !== undefined) refreshToken = tokens.refreshToken;
  generation += 1;
}

export function clearTokens(): void {
  accessToken = null;
  refreshToken = null;
  inFlight = null;
  generation += 1;
}

/**
 * Trade the refresh token for a new access token, or null if there is none to
 * trade (unknown, expired, revoked, or never signed in — the server answers all
 * four the same way, and so does this).
 *
 * SINGLE FLIGHT. A screen that fires six queries at once wakes up to six
 * simultaneous 401s; six refreshes would be six writes to `last_seen_at` and,
 * with rotation later, five of them racing to invalidate the sixth. Every
 * caller waits on the same promise instead.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (inFlight !== null) return inFlight;

  // `mine`, not a bare `inFlight = null`: `clearTokens()` drops the reference,
  // so by the time an abandoned refresh settles a NEWER one may own the slot.
  // Clearing it unconditionally would let a finished old request un-share the
  // live one, and the next caller would open a second concurrent refresh.
  const mine: Promise<string | null> = runRefresh().finally(() => {
    if (inFlight === mine) inFlight = null;
  });
  inFlight = mine;
  return mine;
}

async function runRefresh(): Promise<string | null> {
  const startedIn = generation;
  /** Did the credentials get replaced or dropped while we were in the air? */
  const stale = () => generation !== startedIn;

  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      // `same-origin` is fetch's default and the honest one here: the refresh
      // cookie is scoped to /v1/auth on the API's own origin. A native client
      // does not rely on it at all and sends the token in the body.
      //
      // No `client` field: the endpoint returns an access token and nothing
      // else, so there is nothing for one to select. The refresh token we hold
      // (if any) stays the one we hold — it is not rotated.
      body: JSON.stringify(refreshToken === null ? {} : { refreshToken }),
    });
    if (!res.ok) {
      // A refused refresh is a dead session, not a transient error: drop the
      // stale token rather than letting a caller retry with it forever. Unless
      // these are not the credentials we set out with — then the refusal was
      // about somebody else's session and clearing would sign the new user out.
      if (!stale()) accessToken = null;
      return null;
    }
    // `RefreshResponse` has no refresh-token field at all, so this cannot
    // clobber a native client's stored token — it only ever sets the access one.
    const body = (await res.json()) as RefreshResponse;

    // Checked HERE, with no await between the check and the write, so the
    // window it closes is genuinely closed. Refusing to apply a stale success
    // is the whole point of `generation`: at this exact line an abandoned
    // refresh would otherwise resurrect a token past logout, or overwrite a
    // newer user's. Returning null rather than the token also stops `request()`
    // retrying — a request issued as the old identity must not silently
    // complete as the new one.
    if (stale()) return null;

    setTokens(body.tokens);
    return body.tokens.accessToken;
  } catch {
    // Network failure — say "no token", but keep whatever we had. The caller's
    // request is going to fail on its own terms anyway.
    return null;
  }
}
